/**
 * Feature flags convention: go/frontend-feature-flags
 */
import { expValEquals, expVal, UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { ff, getFeatureFlagValue } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';

export const isNewAutomationMenuEnabled = () =>
	ff('jsw-replace-automationmenu-with-new-context-aware-menu_rltpk');

export const isDevStatusOnCardsDisabled = () => ff('jsw.killswitch.dev-status-on-cards');

export const getBoardPollingInterval = () =>
	getFeatureFlagValue('jsw.nextgen.board.polling.interval', 60000);

export const getBoardJirtDebounceInterval = () =>
	getFeatureFlagValue('jsw.nextgen.board.jirt.debounce.interval', 500);

export const getBoardJirtJitter = () => getFeatureFlagValue('jsw.nextgen.board.jirt.jitter', 500);

export const isErrorTrackingEnabledWithSentry = () => ff('fe.platform.error.tracking.enabled');

export const isPrefetchIssueDataByColumnLimitEnabled = () =>
	ff('jsw-agile-board-prefetch-issue-data-by-column-limit');

export const getCustomFiltersDebounceTime = () =>
	getFeatureFlagValue('jsw.perf.custom-filters-core-tmp.debounce-time', 500);

export const customFiltersRefilteringDebounceIntervalMs = (): number =>
	getFeatureFlagValue('jsw.nextgen.custom-filters.refiltering-debounce-interval-ms', 500);

export const isSyntheticTenant = () => ff('jsw.pollinator.tenants');

// <pyamada>
export const isLongTasksRefactorEnabled = () => ff('jsw.long-tasks-refactor');

// </pyamada>

// <eteowarang
// </eteowarang>

// <whua>
export const isAlternateTTIMeasurementEnabled = () => ff('jsw-measure-tti-alternate');

// </whua>

export const emitApdexOnAnimationFrame = () =>
	ff('jsw.perf.board-backlog-emit-apdex-on-animation-frame');

// <dhuang3>
export const isCmpUifFilterSelectionBarEnabled = (): boolean =>
	ff('jsw.perf.uif.capability.cmp-filter-selection-bar');
// </dhuang3>

export const isBulkEditorMenuCopyEnabled = () =>
	ff('jsw.perf.backlog.bulk-editing.bulk-editor-menu-copy');

export const isLinkIssueFromBoardCardEnabled = (
	{ fireExperimentExposure } = { fireExperimentExposure: false },
): boolean => {
	const [expConfig, fireExposure] = UNSAFE_noExposureExp('jsw_link_issues_from_board_cards');

	if (fireExperimentExposure) {
		fireExposure();
	}

	return (
		// LD feature flag wrapper required to release to Release Track enrolled customers
		// https://app.launchdarkly.com/jira/staging/features/jsw.perf.issue-links-on-board-cards
		ff('jsw.perf.issue-links-on-board-cards') &&
		// Check if Milestone 1 for issue links is enabled
		// https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/gates/jsw_enable_issue_linking_from_jsw_board_cards_m1
		fg('jsw_enable_issue_linking_from_jsw_board_cards_m1') &&
		// check if the experiment is enabled
		// https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/jsw_link_issues_from_board_cards
		expConfig.get('enableIssueLinkExperiment', false)
	);
};

export const INCREMENT_PLANNING_BOARD_M1 =
	'com.atlassian.rm.jpo.jpo3cloud.increment-planning-board-m1';

export const isIncrementPlanningBoardM1Enabled = () => ff(INCREMENT_PLANNING_BOARD_M1);

export const isIssueLinksStatsVisualisationOnBoardCardsEnabled = () => {
	const [expConfig] = UNSAFE_noExposureExp('jsw_link_issues_from_board_cards');

	return (
		// LD feature flag wrapper required to release to Release Track enrolled customers
		ff('jsw.perf.issue-links-on-board-cards') &&
		// Check if Milestone 2 for issue links is enabled
		fg('jsw_enable_issue_links_stats_on_jsw_board_cards_m2') &&
		// check if the experiment is enabled
		expConfig.get('enableIssueLinkExperiment', false)
	);
};

export const isIssueLinksStatsViewSettingsEnabled = (): boolean => {
	const [expConfig] = UNSAFE_noExposureExp('jsw_link_issues_from_board_cards');

	return (
		// LD feature flag wrapper required to release to Release Track enrolled customers
		ff('jsw.perf.issue-links-on-board-cards') &&
		// Check if Milestone 4 for issue links details popup is enabled
		fg('jsw_enable_issue_links_stats_on_view_settings_m4') &&
		// check if the experiment is enabled
		expConfig.get('enableIssueLinkExperiment', false)
	);
};

export const isIssueLinksStatsNudgeEnabled = (): boolean =>
	// LD feature flag wrapper required to release to Release Track enrolled customers
	// Need the precondition to make sure we can turn off this feature without turning off the whole experiment
	// eslint-disable-next-line jira/ff/no-preconditioning
	ff('jsw.perf.issue-links-on-board-cards') &&
	// Check if feature gate for issue links onboarding nudge is enabled
	fg('jsw_enable_issue_links_on_board_cards_nudge') &&
	// check if the experiment is enabled
	expVal('jsw_link_issues_from_board_cards', 'enableIssueLinkExperiment', false);

// eslint-disable-next-line jira/ff/inline-usage
export const isTailoredViewExperimentEnabled = () =>
	expValEquals('jsw_additional_card_fields_in_view_settings', 'cohort', 'variation');

// <tjoy>
// eslint-disable-next-line jira/ff/inline-usage
export function isInAddPeopleButtonOpenTMPControlCohort(): boolean {
	return expValEquals('open_invite_for_open_tmp_projects', 'cohort', 'control');
}
// </tjoy>

// <fzeng>
export const isViewSettingAsPanelExpEnabledWithNoExposure = () => {
	const [expConfig] = UNSAFE_noExposureExp('jsw_additional_card_fields_in_view_settings');
	return expConfig.get('cohort', 'not-enrolled') === 'variation';
};
// </fzeng>

// <hyustiawan>
export const isIssueTypeInContextExperimentEnabled = () => {
	const [expConfig] = UNSAFE_noExposureExp('jsw_config_in_context_issue_types_m2');
	return (
		// kill switch
		ff('config-in-context-issue-type-m2-flag_bddyq') &&
		// check if user is in test / experimentation cohort
		expConfig.get('inlineIssueTypeConfigEnabled', false)
	);
};

// </hyustiawan>
